import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ClampedText } from 'commons/generics/ClampedText';
import {
  Box,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TextField,
  Typography,
  InputAdornment,
  TablePagination,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { paddingCardsTables } from 'commons/values';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import Pagination from '@mui/material/Pagination';

import { useTableState } from 'hooks/useTableState';
import {
  getAllPermissionsStore,
  deletePermissionStore,
  restorePermissionStore,
} from 'store/permissions/thunks';
import { StyledTableCell, StyledTableRow } from 'commons/generics/StyledTableComponents';
import { GenericTableHead, HeadCell } from 'commons/generics/GenericTableHead';
import { SinDatos } from 'commons/generics/SinDatos';
import {
  corregirTexto,
  firstLetterUpperCase,
  hasPemissions,
  usarFuncionesComposicion,
} from 'commons/utils';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';
import { StatusChip } from 'commons/generics/StatusChip';

const getApplicationColor = (appName: string) => {
  const name = appName?.toLowerCase() || '';
  if (name.includes('qihotels')) return { color: '#8E33FF', bgcolor: 'rgba(142, 51, 255, 0.1)' }; // Morado
  if (name.includes('restaurante')) return { color: '#ff6b00', bgcolor: 'rgba(251, 122, 2, 0.1)' }; // Naranja
  if (name.includes('calificaciones'))
    return { color: '#28C76F', bgcolor: 'rgba(40, 199, 111, 0.1)' }; // Verde
  if (name.includes('ordenes de servicio'))
    return { color: '#2933c4ff', bgcolor: 'rgba(0, 207, 232, 0.1)' }; // Cyan/Azul
  if (name.includes('inventario pos'))
    return { color: '#EA5455', bgcolor: 'rgba(234, 84, 85, 0.1)' }; // Rojo
  return { color: '#1877F2', bgcolor: 'rgba(24, 119, 242, 0.1)' }; // Azul por defecto
};

const headCells: HeadCell[] = [
  { id: 'name', label: 'Nombre' },
  { id: 'icon', label: 'Ícono' },
  { id: 'slug', label: 'Slug' },
  { id: 'description', label: 'Descripción' },
  { id: 'application', label: 'Aplicación' },
  { id: 'status', label: 'Estado' },
  { id: 'actions', label: 'Acciones', align: 'center' },
];

export const TablePermissions = () => {
  const navigate = useNavigate();
  const { permissions_list } = useSelector((state: any) => state.permissions);
  const dispatch = useDispatch<any>();

  const {
    dataTableList,
    checked,
    setChecked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleRequestSort,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    updatePaginationFromList,
    handleSwitchChange: handleSwitchChangeHook,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    changePaginationPage: changePaginationPageHook,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    openElem,
    setOpenElem,
    handleResponse,
    ResponseComponents,
    getSortedAndFilteredList,
    isServerSide,
  } = useTableState('permissions.table.permmision');

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canRestore, setCanRestore] = useState(false);

  const checkPermissions = async () => {
    const view = await hasPemissions('modulo.manager', 'modulo.manager.permisos', 'view');
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions('modulo.manager', 'modulo.manager.permisos', 'edit');
    if (edit) setCanEdit(true);

    const del = await hasPemissions('modulo.manager', 'modulo.manager.permisos', 'delete');
    if (del) setCanDelete(true);

    const restore = await hasPemissions('modulo.manager', 'modulo.manager.permisos', 'restore');
    if (restore) setCanRestore(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getInitialData = (statusParam?: number) => {
    dispatch(
      getAllPermissionsStore({
        page: 1,
        take: 10,
        status: statusParam ?? (checked ? 0 : 1),
        param: '',
      })
    );
  };

  useEffect(() => {
    updatePaginationFromList(permissions_list);
  }, [permissions_list, updatePaginationFromList]);

  useEffect(() => {
    if (canView) {
      getInitialData();
    }
  }, [canView]);

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const param = e.target.value;
    setSearchTerm(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) =>
        getAllPermissionsStore({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }),
      getInitialData,
      null,
      'buscar permisos'
    );
  };

  const onResetSearch = () => {
    setSearchTerm('');
    handleResetSearchHook(() => getInitialData());
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChangeHook(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getAllPermissionsStore({ page: 1, take: rowsPerPage, status, param: p }),
        () => getInitialData(status),
        null,
        'buscar permisos'
      );
    } else {
      getInitialData(status);
    }
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    const validPage = value < 1 ? 1 : value;
    changePaginationPageHook(event, validPage);
    const response = await dispatch(
      getAllPermissionsStore({
        page: validPage,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'listar permisos', false);
  };

  const handleView = (row: any) => {
    navigate(`/manager/permisos/${row.id}/detalle`);
  };

  const handleEdit = (row: any) => {
    navigate(`/manager/permisos/${row.id}/editar`);
  };

  const handleDelete = (row: any) => {
    setOpenElem(row);
    setOpenModalDeactivation(true);
  };

  const handleRestoreClick = (row: any) => {
    setOpenElem(row);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      deletePermissionStore(openElem.id, {
        page,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'desactivar permiso', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      restorePermissionStore(openElem.id, {
        page,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'restaurar permiso', true);
    setOpenModalRestore(false);
    setOpenElem(null);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3, mt: 5 }}>
        <Typography variant="h6" color="error">
          No tienes permisos para ver este contenido.
        </Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'description'],
    'status',
    checked ? 0 : 1
  );

  return (
    <Box sx={{ width: '100%', mt: paddingCardsTables }}>
      {ResponseComponents}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={handleSearch}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivos
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="permissions table">
            <GenericTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headCells={headCells}
            />
            <TableBody>
              {sortedList.length > 0 ? (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any) => {
                    return (
                      <StyledTableRow key={row.id}>
                        <StyledTableCell>
                          {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.name)}
                        </StyledTableCell>
                        <StyledTableCell>{row.icon}</StyledTableCell>
                        <StyledTableCell>{row.slug}</StyledTableCell>
                        <StyledTableCell>
                          <ClampedText
                            text={usarFuncionesComposicion(
                              corregirTexto,
                              firstLetterUpperCase,
                              row.description
                            )}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {row.application?.name ? (
                            <Chip
                              label={row.application.name}
                              size="small"
                              sx={{
                                bgcolor: getApplicationColor(row.application.name).bgcolor,
                                color: getApplicationColor(row.application.name).color,
                                fontWeight: 700,
                                borderRadius: '8px',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              N/A
                            </Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          <StatusChip status={row.status} />
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canView && handleView(row)}
                                disabled={!canView}
                                color="info"
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            <span
                              style={{
                                cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => canEdit && row.status === 1 && handleEdit(row)}
                                disabled={!canEdit || row.status !== 1}
                                color="primary"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            {row.status === 1 ? (
                              <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canDelete && handleDelete(row)}
                                  disabled={!canDelete}
                                  color="error"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            ) : (
                              <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canRestore && handleRestoreClick(row)}
                                  disabled={!canRestore}
                                  color="success"
                                >
                                  <RestoreOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={7}>
                    <SinDatos />
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {dataTableList && dataTableList.length > 0 && permissions_list?.meta && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={permissions_list.meta.itemCount}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
          />
        )}

        {dataTableList && dataTableList.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1,
              padding: 2.5,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Pagination
              sx={{ justifyContent: 'center' }}
              color="primary"
              count={itemCountPagination}
              page={pagePagination}
              onChange={changePaginationPage}
              showFirstButton
              showLastButton
              shape="circular"
            />
          </Box>
        )}
      </Card>

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        title="¿Estás seguro que deseas desactivar este permiso?"
        description="Este permiso pasará a estado inactivo y no podrá ser usado."
        onConfirm={confirmDeactivate}
      />

      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        title="¿Estás seguro que deseas restaurar este permiso?"
        description="Este permiso pasará a estado activo."
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
